import { useState, useEffect } from 'react';
import { supabase, Portfolio } from '../../lib/supabase';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createWebPageSchema } from '../../utils/seo';

export default function PortfolioPage() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('전체');
  const [isLoading, setIsLoading] = useState(true);

  useSEO({
    title: 'Portfolio - KR STUDIO | 3,100개 이상의 성공 프로젝트',
    description: 'KR스튜디오의 3,100개 이상의 성공적인 프로젝트 포트폴리오를 확인하세요. 대학, 공공기관, 기업 등 다양한 분야의 콘텐츠 제작 사례를 소개합니다.',
    keywords: '포트폴리오, 프로젝트 사례, 콘텐츠 제작 사례, 이러닝 포트폴리오, KR스튜디오 실적',
    canonical: '/portfolio',
    schema: createWebPageSchema(
      'Portfolio - KR STUDIO',
      'KR스튜디오의 3,100개 이상의 성공적인 프로젝트 포트폴리오',
      '/portfolio'
    ),
  });

  useEffect(() => {
    loadPortfolios();
  }, []);

  const loadPortfolios = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('portfolios')
        .select('*')
        .order('year', { ascending: false });

      if (error) throw error;
      setPortfolios(data || []);
    } catch (error) {
      console.error('포트폴리오 로딩 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatYear = (year: string) => {
    if (year.includes('~')) {
      const [startYear] = year.split('~');
      return `${startYear}년~현재`;
    }
    return `${year}년`;
  };

  const years = ['all', ...Array.from(new Set(portfolios.map(p => p.year)))];

  const filteredPortfolios = portfolios;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-semibold mb-6 whitespace-nowrap">
              PORTFOLIO
            </span>
            <h1 className="text-5xl md:text-6xl font-black mb-6 leading-tight">
              Success Stories
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              3,100개 이상의 프로젝트를 성공적으로 완수한 KR스튜디오의<br />
              다양한 포트폴리오를 확인해보세요
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-black text-gray-900 mb-2">3,100+</div>
              <div className="text-gray-600 text-sm">누적 프로젝트</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-gray-900 mb-2">98%</div>
              <div className="text-gray-600 text-sm">납기 준수율</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-gray-900 mb-2">250+</div>
              <div className="text-gray-600 text-sm">협력 기업</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-gray-900 mb-2">15년</div>
              <div className="text-gray-600 text-sm">업계 경력</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section - Hidden */}
      <section className="hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedYear === year
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {year === 'all' ? '전체' : formatYear(year)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-6">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-500 mt-4">포트폴리오를 불러오는 중...</p>
            </div>
          ) : filteredPortfolios.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">등록된 포트폴리오가 없습니다</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPortfolios.map((portfolio) => (
                <div
                  key={portfolio.id}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
                >
                  <div className="relative h-64 overflow-hidden bg-gray-100 flex items-center justify-center">
                    {portfolio.logo_url ? (
                      <img
                        src={portfolio.logo_url}
                        alt={portfolio.company_name}
                        className="w-full h-full object-contain p-8 transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="text-gray-400">
                        <i className="ri-building-line text-6xl"></i>
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        {formatYear(portfolio.year)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-3">
                      <span className="text-sm font-semibold text-blue-600">{portfolio.company_name}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {portfolio.project_name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                      {portfolio.work_description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-6">
            다음 성공 사례의 주인공은<br />당신의 프로젝트입니다
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            KR스튜디오와 함께 성공적인 프로젝트를 만들어보세요
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 hidden">
            <a
              href="tel:02-6952-7400"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              상담 문의하기
            </a>
            <a
              href="#"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              이메일 문의
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <QuickMenu />
    </div>
  );
}
